import { DIRECTORY_GROUPS } from './types.js';
import { DATA_STATUS_VALUES } from './data-status.js';
// ─────────────────────────────── Хто заповнює (пакет вхідних даних) ───────────────────────────────
const WHO_OFFICE = 'Навчальний відділ';
const WHO_ADMIN = 'Адміністратор навчального процесу';
const WHO_DEAN = 'Деканат';
const WHO_DEPT = 'Кафедра';
const WHO_FACILITIES = 'Адміністрація / господарський підрозділ';
const WHO_SYSTEM = 'Система (розрахунок) / адміністратор';
// ─────────────────────────────── Будівельники полів ───────────────────────────────
const CODE_RULE = "Унікальний стабільний код; зв'язки між файлами — лише за кодом (D4)";
const code = (example, rule = CODE_RULE) => ({
    key: 'code',
    label: 'Код',
    type: 'string',
    required: true,
    unique: true,
    example,
    rule,
    width: 16,
});
const str = (key, label, extra = {}) => ({
    key,
    label,
    type: 'string',
    width: 24,
    ...extra,
});
const text = (key, label, extra = {}) => ({
    key,
    label,
    type: 'text',
    width: 36,
    hidden: true,
    ...extra,
});
const int = (key, label, extra = {}) => ({
    key,
    label,
    type: 'int',
    width: 10,
    ...extra,
});
const bool = (key, label, extra = {}) => ({
    key,
    label,
    type: 'bool',
    example: 'ні',
    rule: 'так / ні',
    width: 10,
    ...extra,
});
const date = (key, label, extra = {}) => ({
    key,
    label,
    type: 'date',
    rule: 'Дата ДД.ММ.РРРР або дата Excel',
    width: 12,
    ...extra,
});
const time = (key, label, extra = {}) => ({
    key,
    label,
    type: 'time',
    rule: 'Час ГГ:ХХ (24-годинний)',
    width: 8,
    ...extra,
});
const ref = (key, label, target, extra = {}) => ({
    key,
    label,
    type: 'ref',
    ref: target,
    width: 16,
    ...extra,
});
const dict = (key, label, dictionary, extra = {}) => ({
    key,
    label,
    type: 'dictItem',
    dictionary,
    width: 16,
    ...extra,
});
const enumField = (key, label, enumName, extra = {}) => ({
    key,
    label,
    type: 'enum',
    enumName,
    width: 14,
    ...extra,
});
const isActive = bool('isActive', 'Активний', {
    example: 'так',
    rule: 'так / ні; неактивні записи не пропонуються у виборі',
    hidden: true,
});
const sortOrder = int('sortOrder', 'Порядок', { example: '10', hidden: true });
const note = text('note', 'Примітка');
const url = str('url', 'Сторінка на сайті', { example: 'https://example.edu.ua/…', hidden: true, width: 30 });
const phone = str('phone', 'Телефон', { example: '+380 32 000 00 00', hidden: true, width: 18 });
const email = str('email', 'Ел. пошта', { example: 'dept@example.edu.ua', hidden: true, rule: 'Адреса електронної пошти' });
const weekday = int('weekday', 'День тижня', { required: true, min: 1, max: 7, example: '1', rule: '1 = Пн … 7 = Нд' });
// ─────────────────────────────── Класифікатори ───────────────────────────────
function classifier(code, title, titleSingular, sheet, description, example, metaFields = []) {
    return {
        code,
        kind: 'dictionary',
        title,
        titleSingular,
        description,
        group: 'Класифікатори',
        prismaModel: 'DictionaryItem',
        sheet,
        who: WHO_ADMIN,
        fields: [
            {
                key: 'code',
                label: 'Код',
                type: 'string',
                required: true,
                unique: true,
                example: example[0],
                rule: 'Латиниця у верхньому регістрі, цифри, «_»; не змінюється після створення (на код спирається логіка)',
                width: 20,
            },
            { key: 'label', label: 'Назва', type: 'string', required: true, example: example[1], width: 40 },
            ...metaFields,
            bool('meta.isDefault', 'За замовчуванням', { hidden: true, rule: 'так — значення за замовчуванням у формах (одне на класифікатор)' }),
            str('color', 'Колір', { hidden: true, width: 10, example: '#1f4fd1', rule: 'Колір у форматі #RRGGBB (необов’язково)' }),
            sortOrder,
            isActive,
        ],
        dependsOn: [],
        titleField: 'label',
        searchFields: ['code', 'label'],
        defaultSort: { field: 'sortOrder', dir: 'asc' },
        naturalKey: ['code'],
        softDelete: true,
    };
}
const CLASSIFIERS = [
    classifier('lesson_type', 'Види занять', 'Вид заняття', 'K01_Lesson_Types', 'Вид заняття визначає типовий контингент (потік / група / підгрупа), потребу в аудиторії, колонку годин навчального плану і колір у календарі.', ['LAB', 'Лабораторне заняття'], [
        enumField('meta.defaultAudience', 'Типовий контингент', 'AudienceKind', {
            required: true,
            example: 'SUBGROUP',
            rule: 'STREAM — потік, GROUP — група, SUBGROUP — підгрупа',
        }),
        bool('meta.needsRoom', 'Потрібна аудиторія', { example: 'так' }),
        str('meta.hoursField', 'Колонка годин плану', {
            hidden: true,
            example: 'hoursLab',
            rule: 'Технічне ім’я колонки годин рядка навчального плану (hoursLecture, hoursPractical, …)',
        }),
        str('meta.shortLabel', 'Скорочення', { example: 'Лаб', width: 10 }),
    ]),
    classifier('room_type', 'Типи аудиторій', 'Тип аудиторії', 'K02_Room_Types', 'Тип аудиторії порівнюється з вимогами заняття (матриця вимог, навантаження).', ['COMPUTER', "Комп'ютерний клас"], [bool('meta.isVirtual', 'Віртуальна (онлайн)', { hidden: true })]),
    classifier('equipment', 'Обладнання', 'Обладнання', 'K03_Equipment', 'Обладнання аудиторій і вимоги занять до обладнання.', ['MICROSCOPE', 'Мікроскопи'], [bool('meta.countable', 'Має кількість', { rule: 'так — у аудиторії важлива кількість одиниць (ПК, мікроскопи)' })]),
    classifier('location_kind', 'Типи локацій', 'Тип локації', 'K04_Location_Kinds', 'Навчальні корпуси, клінічні бази (лікарні міста), симуляційні центри тощо (D20).', ['CLINICAL_BASE', 'Клінічна база'], [bool('meta.isClinical', 'Клінічна база')]),
    classifier('unit_kind', 'Типи структурних підрозділів', 'Тип підрозділу', 'K05_Unit_Kinds', 'Тип підрозділу верхнього рівня: факультет, інститут, коледж, центр.', ['FACULTY', 'Факультет']),
    classifier('education_level', 'Рівні вищої освіти', 'Рівень освіти', 'K06_Education_Levels', 'Рівень вищої освіти освітньої програми.', ['INTEGRATED_MASTER', 'Магістр (інтегрована програма)'], [int('meta.nqf', 'Рівень НРК', { min: 5, max: 8, example: '7', rule: 'Рівень Національної рамки кваліфікацій' })]),
    classifier('study_form', 'Форми здобуття освіти', 'Форма навчання', 'K07_Study_Forms', 'Форма навчання освітньої програми.', ['FULL_TIME', 'Денна (очна)']),
    classifier('language', 'Мови викладання', 'Мова', 'K08_Languages', 'Мова групи й мови викладача порівнюються в перевірці навантаження (конфлікт C13, D19).', ['EN', 'Англійська'], [str('meta.iso', 'Код ISO 639-1', { example: 'en', width: 8 })]),
    classifier('control_form', 'Форми підсумкового контролю', 'Форма контролю', 'K09_Control_Forms', 'Форма підсумкового контролю рядка навчального плану.', ['EXAM', 'Екзамен'], [bool('meta.isExam', 'Екзамен', { hidden: true })]),
    classifier('position', 'Посади викладачів', 'Посада', 'K10_Positions', 'Посада науково-педагогічного працівника.', ['ASSOCIATE_PROFESSOR', 'Доцент']),
    classifier('academic_degree', 'Наукові ступені', 'Науковий ступінь', 'K11_Academic_Degrees', 'Науковий ступінь викладача.', [
        'DSC',
        'Доктор наук',
    ]),
    classifier('academic_title', 'Вчені звання', 'Вчене звання', 'K12_Academic_Titles', 'Вчене звання викладача.', ['DOCENT', 'Доцент']),
    classifier('calendar_period_type', 'Типи періодів календаря', 'Тип періоду', 'K13_Period_Types', 'Тип періоду графіка освітнього процесу: навчання, канікули, сесія, практика, КРОК, атестація, резерв, дистанційне навчання (D8).', ['VACATION', 'Канікули'], [
        bool('meta.blocksTeaching', 'Блокує заняття', { rule: 'так — у період не ставляться звичайні заняття' }),
        bool('meta.forceOnline', 'Лише онлайн', { hidden: true, rule: 'так — у період заняття проводяться лише онлайн' }),
    ]),
    classifier('stream_kind', 'Типи потоків', 'Тип потоку', 'K14_Stream_Kinds', 'Тип потоку; вибіркові дисципліни MVP — потоки ELECTIVE, сформовані деканатом (D40).', ['ELECTIVE', 'Вибіркова дисципліна'], [bool('meta.elective', 'Вибіркова', { hidden: true })]),
    classifier('lesson_format', 'Формати занять', 'Формат заняття', 'K15_Lesson_Formats', 'Очно / онлайн / змішано. Онлайн-заняття не потребує аудиторії групі (D22).', ['ONLINE', 'Онлайн'], [bool('meta.needsRoom', 'Потрібна аудиторія', { example: 'ні' })]),
];
// ─────────────────────────────── Структурні довідники ───────────────────────────────
const ENTITIES = [
    // ── Календар і час: сітка пар (потрібна університету й локаціям) ──
    {
        code: 'time_grids',
        kind: 'entity',
        title: 'Часові сітки',
        titleSingular: 'Часова сітка',
        description: 'Розклад дзвінків. Сітка за замовчуванням задається в «Університет»; клінічна база може мати свою (D7).',
        group: 'Календар і час',
        prismaModel: 'TimeGrid',
        sheet: '01_Time_Grids',
        who: WHO_OFFICE,
        fields: [code('STD'), str('name', 'Назва', { required: true, example: 'Стандартна сітка пар', width: 30 }), note, isActive],
        dependsOn: [],
        titleField: 'name',
        searchFields: ['code', 'name'],
        defaultSort: { field: 'code', dir: 'asc' },
        softDelete: true,
    },
    {
        code: 'time_slots',
        kind: 'entity',
        title: 'Пари (часові слоти)',
        titleSingular: 'Пара',
        description: 'Пари часової сітки: номер, початок, кінець (пакет даних №20.4).',
        group: 'Календар і час',
        prismaModel: 'TimeSlot',
        sheet: '02_Time_Slots',
        who: WHO_OFFICE,
        fields: [
            code('STD-1', 'Код сітки + номер пари, напр. STD-1'),
            ref('timeGridId', 'Часова сітка', 'time_grids', { required: true, example: 'STD' }),
            int('index', 'Номер пари', { required: true, min: 1, max: 8, example: '1' }),
            time('startTime', 'Початок', { required: true, example: '08:30' }),
            time('endTime', 'Кінець', { required: true, example: '09:50', rule: 'Час ГГ:ХХ, пізніше за початок' }),
            str('label', 'Підпис', { example: '1 пара', width: 12 }),
        ],
        dependsOn: ['time_grids'],
        titleField: 'label',
        searchFields: ['code', 'label'],
        defaultSort: { field: 'index', dir: 'asc' },
        softDelete: false,
        parent: { directory: 'time_grids', field: 'timeGridId' },
    },
    // ── Структура ──
    {
        code: 'university',
        kind: 'entity',
        title: 'Університет',
        titleSingular: 'Університет',
        description: 'Реквізити університету й глобальні параметри розкладу: академічних годин у парі, робочі дні, пізні пари, публічний розклад (D1, D7, D8).',
        group: 'Структура',
        prismaModel: 'University',
        sheet: '03_University',
        who: WHO_OFFICE,
        single: true,
        fields: [
            code('UNIVERSITY'),
            str('name', 'Повна назва', { required: true, example: 'Національний університет «Приклад»', width: 50 }),
            str('nameEn', 'Назва англійською', { example: 'National University "Example"', hidden: true, width: 40 }),
            str('shortName', 'Скорочена назва', { example: 'НУ «Приклад»' }),
            str('edrpou', 'Код ЄДРПОУ', { example: '12345678', rule: '8 цифр', width: 12 }),
            text('legalAddress', 'Юридична адреса', { example: '01001, м. Київ, вул. Прикладна, 1' }),
            str('subordination', 'Підпорядкування', { example: "Міністерство охорони здоров'я України", hidden: true }),
            str('rectorName', 'Ректор', { example: "Прізвище Ім'я По батькові", hidden: true }),
            str('website', 'Сайт', { example: 'https://example.edu.ua', hidden: true }),
            email,
            phone,
            int('academicHoursPerPair', 'Академічних годин у парі', { min: 1, max: 4, example: '2', rule: 'Ціле 1–4; використовується для розрахунку пар (D10)' }),
            int('workingWeekdays', 'Робочі дні тижня', { array: true, min: 1, max: 7, example: '1,2,3,4,5', rule: '1 = Пн … 7 = Нд, через кому' }),
            ref('defaultTimeGridId', 'Сітка пар за замовчуванням', 'time_grids', { example: 'STD' }),
            int('lateSlotFromIndex', 'Пізні пари з номера', { min: 1, max: 8, example: '5' }),
            bool('publicSchedule', 'Публічний розклад без входу', { rule: 'так — опублікований розклад доступний гостям (D38)' }),
        ],
        dependsOn: ['time_grids'],
        titleField: 'name',
        searchFields: ['code', 'name', 'shortName'],
        defaultSort: { field: 'code', dir: 'asc' },
        softDelete: false,
    },
    // ── Ресурси: локації ──
    {
        code: 'locations',
        kind: 'entity',
        title: 'Локації (корпуси й клінічні бази)',
        titleSingular: 'Локація',
        description: 'Навчальні корпуси, клінічні бази (лікарні міста), симуляційні центри з адресою і координатами для матриці переміщень (D20).',
        group: 'Ресурси',
        prismaModel: 'Location',
        sheet: '04_Locations',
        who: WHO_FACILITIES,
        hasDemoFlag: true,
        fields: [
            code('LOC-01'),
            str('name', 'Назва', { required: true, example: 'Головний корпус', width: 36 }),
            str('shortName', 'Коротка назва', { example: 'Гол. корпус', width: 14 }),
            dict('kindId', 'Тип локації', 'location_kind', { required: true, example: 'UNIVERSITY_BUILDING' }),
            text('address', 'Адреса', { example: '01001, м. Київ, вул. Прикладна, 1', hidden: false }),
            str('city', 'Місто', { example: 'Київ', width: 12 }),
            { key: 'lat', label: 'Широта', type: 'decimal', min: -90, max: 90, example: '50.450100', rule: 'Десяткові градуси (WGS 84)', width: 11 },
            { key: 'lon', label: 'Довгота', type: 'decimal', min: -180, max: 180, example: '30.523400', rule: 'Десяткові градуси (WGS 84)', width: 11 },
            str('coordSource', 'Джерело координат', { example: 'карта', hidden: true, width: 14 }),
            ref('timeGridId', 'Своя сітка пар', 'time_grids', { hidden: true, example: 'CLIN', rule: 'Порожньо — сітка університету' }),
            time('openFrom', 'Працює з', { hidden: true, example: '08:00' }),
            time('openTo', 'Працює до', { hidden: true, example: '20:00' }),
            str('responsibleName', 'Відповідальна особа', { hidden: true }),
            phone,
            url,
            isActive,
        ],
        dependsOn: ['location_kind', 'time_grids'],
        titleField: 'name',
        searchFields: ['code', 'name', 'shortName', 'address'],
        defaultSort: { field: 'code', dir: 'asc' },
        softDelete: true,
        customPage: 'locations',
    },
    {
        code: 'travel_matrix',
        kind: 'entity',
        title: 'Матриця переміщень',
        titleSingular: 'Переміщення',
        description: 'Час переміщення між локаціями (обидва напрямки): пішки й авто — маршрути (OSRM), громадський транспорт — GTFS або оцінка; час студента = min(пішки, транспорт) + 5 хв, викладача = min(пішки, авто + 5, транспорт) + 5 хв (D43). Розрахунок з координат — D21. Значення, задані вручну (ADMIN), перерахунок не перезаписує.',
        group: 'Ресурси',
        prismaModel: 'LocationTravel',
        sheet: '05_Travel_Matrix',
        who: WHO_SYSTEM,
        fields: [
            ref('fromId', 'Звідки', 'locations', { required: true, column: 'from_location_code', example: 'LOC-01' }),
            ref('toId', 'Куди', 'locations', { required: true, column: 'to_location_code', example: 'LOC-02', rule: 'Інша локація, ніж «Звідки»' }),
            int('distanceM', 'Відстань, м', { min: 0, example: '650' }),
            int('walkMin', 'Пішки, хв', { min: 0, example: '8' }),
            int('transportMin', 'Транспортом, хв', { min: 0, example: '3' }),
            int('walkDistanceM', 'Пішки, м', { min: 0, example: '700', hidden: true }),
            int('carMin', 'Авто, хв', { min: 0, example: '4' }),
            int('carDistanceM', 'Авто, м', { min: 0, example: '900', hidden: true }),
            int('transitMin', 'Громадський транспорт, хв', { min: 0, example: '12' }),
            str('transitSource', 'Джерело часу транспорту', {
                example: 'GTFS',
                rule: 'GTFS — розклад транспорту; ESTIMATE — оцінка; WALK — пішки не довше',
                hidden: true,
                width: 12,
            }),
            int('studentMin', 'Час студента, хв', { min: 0, example: '13', rule: 'min(пішки, транспорт) + 5 хв; генератор і C08 беруть для груп' }),
            int('teacherMin', 'Час викладача, хв', { min: 0, example: '9', rule: 'min(пішки, авто + 5, транспорт) + 5 хв' }),
            int('recommendedMin', 'Рекомендований час (= студент), хв', {
                min: 0,
                example: '13',
                rule: '= час студента; порожньо — система підставить час студента',
                filledByHook: true,
            }),
            enumField('source', 'Джерело', 'TravelSource', { example: 'ADMIN', rule: 'CALC — розрахунок, ADMIN — вручну (не перезаписується), API — сервіс маршрутів' }),
            note,
        ],
        dependsOn: ['locations'],
        titleField: 'fromId',
        searchFields: [],
        defaultSort: { field: 'fromId', dir: 'asc' },
        naturalKey: ['fromId', 'toId'],
        softDelete: false,
        customPage: 'travel-matrix',
    },
    // ── Структура: факультети й кафедри ──
    {
        code: 'faculties',
        kind: 'entity',
        title: 'Факультети / інститути',
        titleSingular: 'Факультет',
        description: 'Структурні підрозділи верхнього рівня (пакет даних №2).',
        group: 'Структура',
        prismaModel: 'Faculty',
        sheet: '06_Faculties',
        who: WHO_OFFICE,
        fields: [
            code('FAC-01'),
            str('name', 'Назва', { required: true, example: 'Медичний факультет №1', width: 36 }),
            str('nameEn', 'Назва англійською', { example: 'Medical Faculty No. 1', hidden: true, width: 30 }),
            str('shortName', 'Коротка назва', { example: 'МФ1', width: 10 }),
            dict('kindId', 'Тип підрозділу', 'unit_kind', { required: true, example: 'FACULTY' }),
            str('deanName', 'Декан / директор', { example: "Прізвище Ім'я По батькові" }),
            str('scheduleResponsibleName', 'Відповідальний за розклад', { hidden: true }),
            text('address', 'Адреса деканату', { example: 'м. Київ, вул. Прикладна, 1' }),
            phone,
            email,
            ref('primaryLocationId', 'Основна локація', 'locations', { example: 'LOC-01' }),
            url,
            sortOrder,
            isActive,
        ],
        dependsOn: ['unit_kind', 'locations'],
        titleField: 'name',
        searchFields: ['code', 'name', 'shortName'],
        defaultSort: { field: 'sortOrder', dir: 'asc' },
        softDelete: true,
    },
    {
        code: 'departments',
        kind: 'entity',
        title: 'Кафедри',
        titleSingular: 'Кафедра',
        description: 'Кафедри з основною локацією і додатковими клінічними базами (пакет даних №2, D20).',
        group: 'Структура',
        prismaModel: 'Department',
        sheet: '07_Departments',
        who: WHO_OFFICE,
        fields: [
            code('DEP-001'),
            str('name', 'Назва', { required: true, example: 'Кафедра нормальної анатомії', width: 44 }),
            str('shortName', 'Коротка назва', { hidden: true, width: 14 }),
            ref('facultyId', 'Факультет', 'faculties', { required: true, example: 'FAC-01' }),
            str('headName', 'Завідувач', { example: "Прізвище Ім'я По батькові" }),
            str('scheduleResponsibleName', 'Відповідальний за розклад', { hidden: true }),
            phone,
            email,
            ref('primaryLocationId', 'Основна локація', 'locations', { example: 'LOC-01' }),
            {
                key: 'locations',
                label: 'Додаткові бази',
                type: 'refMany',
                ref: 'locations',
                column: 'extra_location_codes',
                example: 'LOC-12,LOC-15',
                rule: 'Коди локацій через кому',
                through: { relation: 'locations', model: 'DepartmentLocation', ownKey: 'departmentId', otherKey: 'locationId' },
                width: 24,
            },
            url,
            sortOrder,
            isActive,
        ],
        dependsOn: ['faculties', 'locations'],
        titleField: 'name',
        searchFields: ['code', 'name', 'shortName', 'headName'],
        defaultSort: { field: 'name', dir: 'asc' },
        softDelete: true,
    },
    // ── Освіта ──
    {
        code: 'specialties',
        kind: 'entity',
        title: 'Спеціальності',
        titleSingular: 'Спеціальність',
        description: 'Спеціальності з кодами за переліками 2024 і 2015 років (пакет даних №3).',
        group: 'Освіта',
        prismaModel: 'Specialty',
        sheet: '08_Specialties',
        who: WHO_OFFICE,
        fields: [
            code('SPEC-222'),
            str('codeNew', 'Код (перелік 2024)', { example: 'I2', width: 10 }),
            str('codeOld', 'Код (перелік 2015)', { example: '222', width: 10 }),
            str('name', 'Назва', { required: true, example: 'Медицина', width: 30 }),
            str('fieldOfStudy', 'Галузь знань', { example: "Охорона здоров'я та соціальне забезпечення" }),
            isActive,
        ],
        dependsOn: [],
        titleField: 'name',
        searchFields: ['code', 'codeNew', 'codeOld', 'name'],
        defaultSort: { field: 'code', dir: 'asc' },
        softDelete: true,
    },
    {
        code: 'programs',
        kind: 'entity',
        title: 'Освітні програми',
        titleSingular: 'Освітня програма',
        description: 'Освітні програми: спеціальність, рівень, форма, мова, строк навчання, факультет (пакет даних №3).',
        group: 'Освіта',
        prismaModel: 'EducationalProgram',
        sheet: '09_Educational_Programs',
        who: WHO_OFFICE,
        fields: [
            code('OP-01'),
            str('name', 'Назва', { required: true, example: 'Медицина', width: 36 }),
            str('nameEn', 'Назва англійською', { example: 'Medicine', hidden: true }),
            ref('specialtyId', 'Спеціальність', 'specialties', { required: true, example: 'SPEC-222' }),
            dict('levelId', 'Рівень освіти', 'education_level', { required: true, example: 'INTEGRATED_MASTER' }),
            dict('formId', 'Форма навчання', 'study_form', { required: true, example: 'FULL_TIME' }),
            int('durationMonths', 'Строк навчання, міс.', { min: 1, example: '70' }),
            dict('languageId', 'Мова навчання', 'language', { required: true, example: 'UK' }),
            ref('facultyId', 'Факультет', 'faculties', { required: true, example: 'FAC-01' }),
            str('guarantorName', 'Гарант ОП', { hidden: true }),
            url,
            isActive,
        ],
        dependsOn: ['specialties', 'education_level', 'study_form', 'language', 'faculties'],
        titleField: 'name',
        searchFields: ['code', 'name', 'nameEn'],
        defaultSort: { field: 'name', dir: 'asc' },
        softDelete: true,
    },
    {
        code: 'subjects',
        kind: 'entity',
        title: 'Дисципліни',
        titleSingular: 'Дисципліна',
        description: 'Централізований реєстр навчальних дисциплін (пакет даних №6).',
        group: 'Освіта',
        prismaModel: 'Subject',
        sheet: '10_Subjects',
        who: `${WHO_OFFICE} / ${WHO_DEPT}`,
        hasDemoFlag: true,
        fields: [
            code('SUB-0001'),
            str('name', 'Назва', { required: true, example: 'Анатомія людини', width: 40 }),
            str('nameEn', 'Назва англійською', { example: 'Human Anatomy', hidden: true }),
            str('shortName', 'Коротка назва', { example: 'Анатомія', width: 16 }),
            ref('departmentId', 'Кафедра', 'departments', { example: 'DEP-001' }),
            isActive,
        ],
        dependsOn: ['departments'],
        titleField: 'name',
        searchFields: ['code', 'name', 'shortName', 'nameEn'],
        defaultSort: { field: 'name', dir: 'asc' },
        softDelete: true,
    },
    {
        code: 'curricula',
        kind: 'entity',
        title: 'Навчальні плани',
        titleSingular: 'Навчальний план',
        description: 'Реєстр навчальних планів з версіями: план року вступу не змінюється після затвердження нового (пакет даних №4).',
        group: 'Освіта',
        prismaModel: 'Curriculum',
        sheet: '11_Curriculums',
        who: WHO_OFFICE,
        hasDemoFlag: true,
        fields: [
            code('CUR-2025-001'),
            ref('programId', 'Освітня програма', 'programs', { required: true, example: 'OP-01' }),
            int('intakeYear', 'Рік вступу', { required: true, min: 2000, max: 2100, example: '2025' }),
            int('version', 'Версія', { min: 1, example: '1', rule: 'Унікальна в межах програми й року вступу' }),
            date('approvedAt', 'Дата затвердження', { example: '20.05.2025' }),
            str('approvedBy', 'Ким затверджено', { example: 'Вчена рада, протокол №5', hidden: true }),
            enumField('status', 'Статус плану', 'CurriculumStatus', { example: 'APPROVED' }),
            str('documentUrl', 'Посилання на документ', { hidden: true, width: 30 }),
            date('validFrom', 'Діє з', { hidden: true }),
            date('validTo', 'Діє до', { hidden: true }),
        ],
        dependsOn: ['programs'],
        titleField: 'code',
        searchFields: ['code'],
        defaultSort: { field: 'intakeYear', dir: 'desc' },
        softDelete: true,
        customPage: 'curriculum',
    },
    {
        code: 'curriculum_items',
        kind: 'entity',
        title: 'Рядки навчальних планів',
        titleSingular: 'Рядок плану',
        description: 'Деталізований навчальний план: кожен рядок — освітній компонент у семестрі з розподілом годин за видами занять (пакет даних №5). Редагується на сторінці плану.',
        group: 'Освіта',
        prismaModel: 'CurriculumItem',
        sheet: '12_Curriculum_Subjects',
        who: `${WHO_DEAN} / ${WHO_DEPT}`,
        fields: [
            code('CI-2025-001-012'),
            ref('curriculumId', 'Навчальний план', 'curricula', { required: true, example: 'CUR-2025-001' }),
            ref('subjectId', 'Дисципліна', 'subjects', { required: true, example: 'SUB-0001' }),
            str('componentCode', 'Шифр компонента', { example: 'ОК 12', width: 10 }),
            bool('isElective', 'Вибіркова'),
            int('course', 'Курс', { required: true, min: 1, max: 6, example: '1' }),
            int('semesterNo', 'Семестр', { required: true, min: 1, max: 12, example: '1', rule: 'Номер семестру навчання 1–12' }),
            { key: 'ects', label: 'Кредити ЄКТС', type: 'decimal', min: 0, example: '6', width: 8 },
            int('hoursTotal', 'Усього годин', { min: 0, example: '180' }),
            int('hoursLecture', 'Лекції, год', { min: 0, example: '20' }),
            int('hoursPractical', 'Практичні, год', { min: 0, example: '60' }),
            int('hoursSeminar', 'Семінарські, год', { min: 0, example: '0', hidden: true }),
            int('hoursLab', 'Лабораторні, год', { min: 0, example: '0' }),
            int('hoursClinical', 'Клінічні практичні, год', { min: 0, example: '0' }),
            int('hoursSimulation', 'Симуляційні, год', { min: 0, example: '0', hidden: true }),
            int('hoursConsultation', 'Консультації, год', { min: 0, example: '0', hidden: true }),
            int('hoursIndividual', 'Індивідуальні, год', { min: 0, example: '0', hidden: true }),
            int('hoursPractice', 'Практика, год', { min: 0, example: '0', hidden: true }),
            int('hoursSelfStudy', 'Самостійна робота, год', { min: 0, example: '100' }),
            int('practiceWeeks', 'Тижнів практики', { min: 0, hidden: true }),
            dict('controlFormId', 'Форма контролю', 'control_form', { example: 'EXAM' }),
            enumField('studyMode', 'Режим занять', 'StudyMode', { example: 'WEEKLY', rule: 'WEEKLY — щотижнево, CYCLE — цикл на клінічній кафедрі' }),
            int('cycleDays', 'Днів циклу', { min: 1, example: '10', rule: 'Для режиму CYCLE' }),
            ref('departmentId', 'Кафедра, що викладає', 'departments', { example: 'DEP-001', rule: 'Порожньо — кафедра дисципліни' }),
            note,
        ],
        dependsOn: ['curricula', 'subjects', 'control_form', 'departments'],
        titleField: 'componentCode',
        searchFields: ['code', 'componentCode'],
        defaultSort: { field: 'semesterNo', dir: 'asc' },
        softDelete: false,
        customPage: 'curriculum',
        parent: { directory: 'curricula', field: 'curriculumId' },
    },
    {
        code: 'lesson_requirements',
        kind: 'entity',
        title: 'Вимоги до занять',
        titleSingular: 'Вимога до заняття',
        description: 'Матриця вимог (пакет даних №17): для дисципліни й виду заняття — типи аудиторій, мінімальна місткість, обладнання, формат. Без дисципліни — типова вимога для виду заняття.',
        group: 'Освіта',
        prismaModel: 'LessonRequirement',
        sheet: '13_Lesson_Requirements',
        who: WHO_DEPT,
        hasDemoFlag: true,
        fields: [
            code('REQ-0001'),
            ref('subjectId', 'Дисципліна', 'subjects', { example: 'SUB-0001', rule: 'Порожньо — типова вимога для виду заняття' }),
            dict('lessonTypeId', 'Вид заняття', 'lesson_type', { required: true, example: 'LAB' }),
            {
                key: 'roomTypes',
                label: 'Типи аудиторій',
                type: 'dictItems',
                dictionary: 'room_type',
                column: 'room_type_codes',
                example: 'LAB,COMPUTER',
                rule: 'Коди через кому; будь-який із переліку',
                through: { relation: 'roomTypes', model: 'LessonRequirementRoomType', ownKey: 'requirementId', otherKey: 'roomTypeId' },
                width: 20,
            },
            int('minCapacity', 'Мін. місць', { min: 0, example: '15' }),
            {
                key: 'equipment',
                label: 'Обладнання',
                type: 'dictItems',
                dictionary: 'equipment',
                column: 'equipment_codes',
                example: 'MICROSCOPE',
                rule: 'Коди через кому; потрібне все з переліку',
                through: { relation: 'equipment', model: 'LessonRequirementEquipment', ownKey: 'requirementId', otherKey: 'equipmentId' },
                width: 20,
            },
            dict('formatId', 'Формат', 'lesson_format', { example: 'OFFLINE' }),
            bool('departmentRoomsOnly', 'Лише аудиторії кафедри'),
            bool('teacherRoomRequired', 'Аудиторія викладачу для онлайн', { hidden: true }),
            {
                key: 'locations',
                label: 'Лише локації',
                type: 'refMany',
                ref: 'locations',
                column: 'location_codes',
                example: 'LOC-01',
                rule: 'Коди через кому; порожньо — будь-яка',
                through: { relation: 'locations', model: 'LessonRequirementLocation', ownKey: 'requirementId', otherKey: 'locationId' },
                hidden: true,
                width: 20,
            },
            note,
            isActive,
        ],
        dependsOn: ['subjects', 'lesson_type', 'room_type', 'equipment', 'lesson_format', 'locations'],
        titleField: 'code',
        searchFields: ['code'],
        defaultSort: { field: 'code', dir: 'asc' },
        softDelete: true,
    },
    // ── Ресурси: аудиторії ──
    {
        code: 'rooms',
        kind: 'entity',
        title: 'Аудиторії',
        titleSingular: 'Аудиторія',
        description: 'Аудиторний фонд: локація, номер, тип, місткість, кафедра-власниця (пакет даних №12).',
        group: 'Ресурси',
        prismaModel: 'Room',
        sheet: '14_Rooms',
        who: WHO_FACILITIES,
        hasDemoFlag: true,
        fields: [
            code('RM-0001'),
            ref('locationId', 'Локація', 'locations', { required: true, example: 'LOC-01' }),
            str('number', 'Номер', { required: true, example: '304', width: 10, rule: 'Унікальний у межах локації' }),
            str('name', 'Назва', { example: 'Комп’ютерний клас', hidden: true }),
            int('floor', 'Поверх', { example: '3', width: 8 }),
            dict('roomTypeId', 'Тип аудиторії', 'room_type', { required: true, example: 'COMPUTER' }),
            int('capacity', 'Місткість', { required: true, min: 0, example: '24' }),
            int('seats', 'Кількість місць', { min: 0, example: '24', hidden: true }),
            int('computers', 'Комп’ютерів', { min: 0, example: '24', hidden: true }),
            ref('ownerDepartmentId', 'Кафедра-власниця', 'departments', { example: 'DEP-001', rule: 'Для кафедральних навчальних кімнат' }),
            bool('isBookable', 'Доступна для розкладу', { example: 'так' }),
            note,
            isActive,
        ],
        dependsOn: ['locations', 'room_type', 'departments'],
        titleField: 'number',
        searchFields: ['code', 'number', 'name'],
        defaultSort: { field: 'code', dir: 'asc' },
        softDelete: true,
    },
    {
        code: 'room_equipment',
        kind: 'entity',
        title: 'Обладнання аудиторій',
        titleSingular: 'Обладнання аудиторії',
        description: 'Яке обладнання і скільки є в аудиторії (пакет даних №12, аркуш Room_Equipment).',
        group: 'Ресурси',
        prismaModel: 'RoomEquipment',
        sheet: '15_Room_Equipment',
        who: WHO_FACILITIES,
        fields: [
            ref('roomId', 'Аудиторія', 'rooms', { required: true, example: 'RM-0001' }),
            dict('equipmentId', 'Обладнання', 'equipment', { required: true, example: 'PC' }),
            int('quantity', 'Кількість', { min: 0, example: '24' }),
            note,
        ],
        dependsOn: ['rooms', 'equipment'],
        titleField: 'equipmentId',
        searchFields: [],
        defaultSort: { field: 'roomId', dir: 'asc' },
        naturalKey: ['roomId', 'equipmentId'],
        softDelete: false,
        parent: { directory: 'rooms', field: 'roomId' },
    },
    // ── Календар і час ──
    {
        code: 'academic_years',
        kind: 'entity',
        title: 'Навчальні роки',
        titleSingular: 'Навчальний рік',
        description: 'Навчальні роки (графік освітнього процесу, пакет даних №14).',
        group: 'Календар і час',
        prismaModel: 'AcademicYear',
        sheet: '16_Academic_Years',
        who: WHO_OFFICE,
        fields: [
            code('2026-2027', 'Формат РРРР-РРРР'),
            str('name', 'Назва', { example: '2026/2027 навчальний рік' }),
            date('startDate', 'Початок', { required: true, example: '01.09.2026' }),
            date('endDate', 'Кінець', { required: true, example: '30.06.2027' }),
            bool('isCurrent', 'Поточний', { rule: 'так — лише для одного року' }),
        ],
        dependsOn: [],
        titleField: 'code',
        searchFields: ['code', 'name'],
        defaultSort: { field: 'startDate', dir: 'desc' },
        softDelete: true,
    },
    {
        code: 'semesters',
        kind: 'entity',
        title: 'Семестри',
        titleSingular: 'Семестр',
        description: 'Семестри навчального року. Тиждень семестру рахується від дати початку; непарний — чисельник, парний — знаменник (D8).',
        group: 'Календар і час',
        prismaModel: 'Semester',
        sheet: '17_Semesters',
        who: WHO_OFFICE,
        fields: [
            code('2026-2027-1', 'Формат РРРР-РРРР-N'),
            ref('academicYearId', 'Навчальний рік', 'academic_years', { required: true, example: '2026-2027' }),
            int('number', 'Номер', { required: true, min: 1, max: 2, example: '1' }),
            str('name', 'Назва', { example: 'Осінній семестр 2026/2027' }),
            date('startDate', 'Початок', { required: true, example: '01.09.2026' }),
            date('endDate', 'Кінець', { required: true, example: '31.12.2026' }),
            int('weeks', 'Тижнів', { readOnly: true, rule: 'Обчислюється з дат' }),
        ],
        dependsOn: ['academic_years'],
        titleField: 'name',
        searchFields: ['code', 'name'],
        defaultSort: { field: 'startDate', dir: 'desc' },
        softDelete: true,
    },
    {
        code: 'calendar_periods',
        kind: 'entity',
        title: 'Періоди календаря',
        titleSingular: 'Період календаря',
        description: 'Канікули, сесії, практики, КРОК, атестації, резервні й дистанційні періоди з областю дії (D8).',
        group: 'Календар і час',
        prismaModel: 'CalendarPeriod',
        sheet: '18_Calendar_Periods',
        who: WHO_OFFICE,
        fields: [
            code('CP-2026-001'),
            ref('academicYearId', 'Навчальний рік', 'academic_years', { required: true, example: '2026-2027' }),
            ref('semesterId', 'Семестр', 'semesters', { example: '2026-2027-1' }),
            dict('typeId', 'Тип періоду', 'calendar_period_type', { required: true, example: 'VACATION' }),
            str('name', 'Назва', { required: true, example: 'Зимові канікули', width: 30 }),
            date('startDate', 'Початок', { required: true, example: '29.12.2026' }),
            date('endDate', 'Кінець', { required: true, example: '11.01.2027', rule: 'Не раніше за початок' }),
            enumField('scope', 'Область дії', 'CalendarScope', { example: 'ALL' }),
            ref('facultyId', 'Факультет', 'faculties', { example: 'FAC-01', rule: 'Для області FACULTY' }),
            ref('programId', 'Освітня програма', 'programs', { example: 'OP-01', rule: 'Для області PROGRAM' }),
            int('course', 'Курс', { min: 1, max: 6, example: '6', rule: 'Для області COURSE (разом із програмою — курс програми)' }),
            bool('blocksTeaching', 'Блокує заняття', { rule: 'так / ні; порожньо — за типом періоду' }),
            note,
        ],
        dependsOn: ['academic_years', 'semesters', 'calendar_period_type', 'faculties', 'programs'],
        titleField: 'name',
        searchFields: ['code', 'name'],
        defaultSort: { field: 'startDate', dir: 'asc' },
        softDelete: true,
    },
    {
        code: 'holidays',
        kind: 'entity',
        title: 'Святкові дні',
        titleSingular: 'Святковий день',
        description: 'Святкові й неробочі дні. В умовах воєнного стану святкові дні робочі — «Вихідний» за замовчуванням «ні», адміністратор змінює (D8).',
        group: 'Календар і час',
        prismaModel: 'Holiday',
        sheet: '19_Holidays',
        who: WHO_OFFICE,
        fields: [
            date('date', 'Дата', { required: true, unique: true, example: '25.12.2026' }),
            str('name', 'Назва', { required: true, example: 'Різдво Христове', width: 30 }),
            bool('isDayOff', 'Вихідний', { rule: 'так — заняття не ставляться; у воєнний стан — ні' }),
            date('transferredTo', 'Відпрацювання', { hidden: true }),
        ],
        dependsOn: [],
        titleField: 'name',
        searchFields: ['name'],
        defaultSort: { field: 'date', dir: 'asc' },
        naturalKey: ['date'],
        softDelete: false,
    },
    {
        code: 'room_availability',
        kind: 'entity',
        title: 'Недоступність аудиторій',
        titleSingular: 'Недоступність аудиторії',
        description: 'Коли аудиторію не можна використовувати (ремонт, інші заходи). Жорстке обмеження.',
        group: 'Ресурси',
        prismaModel: 'RoomAvailability',
        sheet: '20_Room_Availability',
        who: WHO_FACILITIES,
        fields: [
            ref('roomId', 'Аудиторія', 'rooms', { required: true, example: 'RM-0001' }),
            ref('semesterId', 'Семестр', 'semesters', { example: '2026-2027-1', rule: 'Порожньо — у всіх семестрах' }),
            weekday,
            ref('timeSlotId', 'Пара', 'time_slots', { example: 'STD-1', rule: 'Код пари; порожньо — увесь день' }),
            enumField('parity', 'Тиждень', 'Parity', { example: 'ALL', rule: 'ALL — щотижня, ODD — чисельник, EVEN — знаменник' }),
            date('dateFrom', 'З дати', { hidden: true }),
            date('dateTo', 'По дату', { hidden: true }),
            text('comment', 'Коментар', { hidden: false }),
        ],
        dependsOn: ['rooms', 'semesters', 'time_slots'],
        titleField: 'roomId',
        searchFields: ['comment'],
        defaultSort: { field: 'roomId', dir: 'asc' },
        naturalKey: ['roomId', 'semesterId', 'weekday', 'timeSlotId', 'parity', 'dateFrom'],
        softDelete: false,
        parent: { directory: 'rooms', field: 'roomId' },
    },
    // ── Контингент ──
    {
        code: 'groups',
        kind: 'entity',
        title: 'Академічні групи',
        titleSingular: 'Група',
        description: 'Академічні групи навчального року: програма, план, курс, кількість студентів, мова (пакет даних №7).',
        group: 'Контингент',
        prismaModel: 'AcademicGroup',
        sheet: '21_Groups',
        who: WHO_DEAN,
        hasDemoFlag: true,
        fields: [
            code('GRP-2026-0001'),
            str('name', 'Назва групи', { required: true, example: '1 мед. 01', width: 14 }),
            ref('academicYearId', 'Навчальний рік', 'academic_years', { required: true, example: '2026-2027' }),
            ref('programId', 'Освітня програма', 'programs', { required: true, example: 'OP-01' }),
            ref('facultyId', 'Факультет', 'faculties', {
                example: 'FAC-02',
                hidden: true,
                rule: 'Порожньо — факультет програми; вказати, якщо групу веде інший факультет (одна програма — кілька факультетів)',
            }),
            ref('curriculumId', 'Навчальний план', 'curricula', { example: 'CUR-2025-001' }),
            int('course', 'Курс', { required: true, min: 1, max: 7, example: '1', rule: '1–6; 7 — інтернатура' }),
            int('studentsCount', 'Студентів', { required: true, min: 0, example: '28' }),
            dict('languageId', 'Мова навчання', 'language', { required: true, example: 'UK' }),
            str('curatorName', 'Куратор', { hidden: true }),
            ref('baseLocationId', 'Базова локація', 'locations', { example: 'LOC-01', hidden: true }),
            int('intakeYear', 'Рік вступу', { min: 2000, max: 2100, example: '2026', hidden: true }),
            isActive,
        ],
        dependsOn: ['academic_years', 'programs', 'faculties', 'curricula', 'language', 'locations'],
        titleField: 'name',
        searchFields: ['code', 'name'],
        defaultSort: { field: 'name', dir: 'asc' },
        softDelete: true,
    },
    {
        code: 'subgroups',
        kind: 'entity',
        title: 'Підгрупи',
        titleSingular: 'Підгрупа',
        description: 'Поділ групи на підгрупи для лабораторних і клінічних занять (ТЗ §7.3).',
        group: 'Контингент',
        prismaModel: 'Subgroup',
        sheet: '22_Subgroups',
        who: WHO_DEAN,
        hasDemoFlag: true,
        fields: [
            code('GRP-2026-0001-A'),
            ref('groupId', 'Група', 'groups', { required: true, example: 'GRP-2026-0001' }),
            str('name', 'Назва підгрупи', { required: true, example: 'А', width: 10, rule: 'Унікальна в межах групи' }),
            int('studentsCount', 'Студентів', { required: true, min: 0, example: '14' }),
            str('criterion', 'Критерій поділу', { example: 'за списком', hidden: true }),
        ],
        dependsOn: ['groups'],
        titleField: 'name',
        searchFields: ['code', 'name'],
        defaultSort: { field: 'code', dir: 'asc' },
        softDelete: true,
        parent: { directory: 'groups', field: 'groupId' },
    },
    {
        code: 'streams',
        kind: 'entity',
        title: 'Потоки',
        titleSingular: 'Потік',
        description: 'Об’єднання груп для лекцій і вибіркових дисциплін (ТЗ §7.2, D40).',
        group: 'Контингент',
        prismaModel: 'Stream',
        sheet: '23_Streams',
        who: WHO_DEAN,
        hasDemoFlag: true,
        fields: [
            code('STR-2026-001'),
            ref('academicYearId', 'Навчальний рік', 'academic_years', { required: true, example: '2026-2027' }),
            str('name', 'Назва', { required: true, example: 'Медицина, 1 курс, потік 1', width: 30 }),
            dict('kindId', 'Тип потоку', 'stream_kind', { required: true, example: 'LECTURE' }),
            ref('programId', 'Освітня програма', 'programs', { example: 'OP-01' }),
            ref('facultyId', 'Факультет', 'faculties', { example: 'FAC-01' }),
            int('course', 'Курс', { min: 1, max: 6, example: '1' }),
            {
                key: 'groups',
                label: 'Групи потоку',
                type: 'refMany',
                ref: 'groups',
                column: 'group_codes',
                example: 'GRP-2026-0001,GRP-2026-0002',
                rule: 'Коди груп через кому',
                through: { relation: 'groups', model: 'StreamGroup', ownKey: 'streamId', otherKey: 'groupId' },
                width: 30,
            },
            int('studentsCount', 'Студентів', { min: 0, example: '84', rule: 'Порожньо — сума студентів груп' }),
            isActive,
        ],
        dependsOn: ['academic_years', 'stream_kind', 'programs', 'faculties', 'groups'],
        titleField: 'name',
        searchFields: ['code', 'name'],
        defaultSort: { field: 'name', dir: 'asc' },
        softDelete: true,
    },
    {
        code: 'students',
        kind: 'entity',
        title: 'Студенти',
        titleSingular: 'Студент',
        description: 'Необов’язковий реєстр студентів (D18): розклад студента = група + підгрупи + потоки.',
        group: 'Контингент',
        prismaModel: 'Student',
        sheet: '24_Students',
        who: WHO_DEAN,
        hasDemoFlag: true,
        fields: [
            code('STU-000001'),
            str('fullName', 'ПІБ', { required: true, example: "Прізвище Ім'я По батькові", width: 30 }),
            { ...email, hidden: false, example: 'student@example.edu.ua', rule: 'Для входу студента (D18)' },
            ref('groupId', 'Група', 'groups', { required: true, example: 'GRP-2026-0001' }),
            {
                key: 'subgroups',
                label: 'Підгрупи',
                type: 'refMany',
                ref: 'subgroups',
                column: 'subgroup_codes',
                example: 'GRP-2026-0001-A',
                rule: 'Коди підгруп через кому',
                through: { relation: 'subgroups', model: 'StudentSubgroup', ownKey: 'studentId', otherKey: 'subgroupId' },
                width: 20,
            },
            {
                key: 'streams',
                label: 'Потоки (вибіркові)',
                type: 'refMany',
                ref: 'streams',
                column: 'stream_codes',
                example: 'STR-2026-014',
                rule: 'Коди потоків через кому',
                through: { relation: 'streams', model: 'StudentStream', ownKey: 'studentId', otherKey: 'streamId' },
                hidden: true,
                width: 20,
            },
            enumField('status', 'Статус', 'StudentStatus', { example: 'ACTIVE' }),
        ],
        dependsOn: ['groups', 'subgroups', 'streams'],
        titleField: 'fullName',
        searchFields: ['code', 'fullName', 'email'],
        defaultSort: { field: 'fullName', dir: 'asc' },
        softDelete: true,
    },
    // ── Викладачі ──
    {
        code: 'teachers',
        kind: 'entity',
        title: 'Викладачі',
        titleSingular: 'Викладач',
        description: 'Реєстр викладачів: кафедра, посада, ступінь, звання, мови, обмеження навантаження (пакет даних №9).',
        group: 'Викладачі',
        prismaModel: 'Teacher',
        sheet: '25_Teachers',
        who: `${WHO_OFFICE} / ${WHO_DEPT}`,
        hasDemoFlag: true,
        fields: [
            code('TCH-0001'),
            str('fullName', 'ПІБ', { required: true, example: 'Викладач Демо 001', width: 30 }),
            ref('departmentId', 'Кафедра', 'departments', { required: true, example: 'DEP-001' }),
            dict('positionId', 'Посада', 'position', { required: true, example: 'ASSOCIATE_PROFESSOR' }),
            dict('degreeId', 'Науковий ступінь', 'academic_degree', { example: 'PHD', hidden: true }),
            dict('titleId', 'Вчене звання', 'academic_title', { example: 'DOCENT', hidden: true }),
            { ...email, rule: 'Для входу викладача й сповіщень' },
            phone,
            {
                key: 'languages',
                label: 'Мови викладання',
                type: 'dictItems',
                dictionary: 'language',
                column: 'language_codes',
                example: 'UK,EN',
                rule: 'Коди через кому (D19)',
                through: { relation: 'languages', model: 'TeacherLanguage', ownKey: 'teacherId', otherKey: 'languageId' },
                width: 12,
            },
            int('maxLoadHours', 'Макс. навантаження, год', { min: 0, example: '600', hidden: true }),
            int('maxPairsPerDay', 'Макс. пар на день', { min: 1, max: 8, example: '4', hidden: true }),
            int('maxTransitionsPerDay', 'Макс. переходів на день', { min: 0, example: '2', hidden: true }),
            ref('primaryLocationId', 'Основна локація', 'locations', { example: 'LOC-01', hidden: true }),
            isActive,
        ],
        dependsOn: ['departments', 'position', 'academic_degree', 'academic_title', 'language', 'locations'],
        titleField: 'fullName',
        searchFields: ['code', 'fullName', 'email'],
        defaultSort: { field: 'fullName', dir: 'asc' },
        softDelete: true,
    },
    {
        code: 'teacher_availability',
        kind: 'entity',
        title: 'Доступність викладачів',
        titleSingular: 'Доступність викладача',
        description: 'Структуровані побажання й заборони (пакет даних №16): FORBIDDEN — «не можна» (жорстке), UNDESIRED / PREFERRED — м’які з пріоритетом.',
        group: 'Викладачі',
        prismaModel: 'TeacherAvailability',
        sheet: '26_Teacher_Availability',
        who: `${WHO_DEPT} / викладач`,
        fields: [
            ref('teacherId', 'Викладач', 'teachers', { required: true, example: 'TCH-0001' }),
            ref('semesterId', 'Семестр', 'semesters', { example: '2026-2027-1', rule: 'Порожньо — у всіх семестрах' }),
            weekday,
            ref('timeSlotId', 'Пара', 'time_slots', { example: 'STD-1', rule: 'Код пари; порожньо — увесь день' }),
            enumField('parity', 'Тиждень', 'Parity', { example: 'ALL' }),
            enumField('kind', 'Тип', 'AvailabilityKind', { required: true, example: 'FORBIDDEN', rule: 'FORBIDDEN — заборонено, UNDESIRED — небажано, PREFERRED — бажано' }),
            int('priority', 'Пріоритет', { min: 0, max: 100, example: '100', rule: '0–100; для FORBIDDEN не впливає' }),
            date('dateFrom', 'З дати', { hidden: true }),
            date('dateTo', 'По дату', { hidden: true }),
            text('comment', 'Коментар', { hidden: false }),
        ],
        dependsOn: ['teachers', 'semesters', 'time_slots'],
        titleField: 'teacherId',
        searchFields: ['comment'],
        defaultSort: { field: 'teacherId', dir: 'asc' },
        naturalKey: ['teacherId', 'semesterId', 'weekday', 'timeSlotId', 'parity', 'kind', 'dateFrom'],
        softDelete: false,
        customPage: 'teacher-availability',
    },
    // ── Навантаження ──
    {
        code: 'department_capacity',
        kind: 'entity',
        title: 'Місткість кафедр для циклів',
        titleSingular: 'Місткість кафедри',
        description: 'Скільки груп кафедра може вести одночасно в циклах у семестрі (клінічні кафедри старших курсів).',
        group: 'Навантаження',
        prismaModel: 'DepartmentCapacity',
        sheet: '27_Department_Capacity',
        who: WHO_DEPT,
        fields: [
            ref('departmentId', 'Кафедра', 'departments', { required: true, example: 'DEP-001' }),
            ref('semesterId', 'Семестр', 'semesters', { required: true, example: '2026-2027-1' }),
            int('maxParallelGroups', 'Груп одночасно', { required: true, min: 1, example: '3' }),
            note,
        ],
        dependsOn: ['departments', 'semesters'],
        titleField: 'departmentId',
        searchFields: [],
        defaultSort: { field: 'departmentId', dir: 'asc' },
        naturalKey: ['departmentId', 'semesterId'],
        softDelete: false,
    },
    {
        code: 'teaching_assignments',
        kind: 'entity',
        title: 'Навчальне навантаження',
        titleSingular: 'Рядок навантаження',
        description: 'Хто, кому, що і скільки проводить (пакет даних №10, D9): дисципліна + вид заняття + контингент + викладачі + години + патерн (щотижнево або цикл).',
        group: 'Навантаження',
        prismaModel: 'TeachingAssignment',
        sheet: '28_Teacher_Load',
        who: `${WHO_DEPT} / ${WHO_OFFICE}`,
        hasDemoFlag: true,
        fields: [
            code('LOAD-2026-00001'),
            ref('semesterId', 'Семестр', 'semesters', { required: true, example: '2026-2027-1' }),
            ref('curriculumItemId', 'Рядок плану', 'curriculum_items', { example: 'CI-2025-001-012', hidden: true }),
            ref('subjectId', 'Дисципліна', 'subjects', { required: true, example: 'SUB-0001' }),
            dict('lessonTypeId', 'Вид заняття', 'lesson_type', { required: true, example: 'PRACTICAL' }),
            ref('departmentId', 'Кафедра', 'departments', { required: true, example: 'DEP-001' }),
            enumField('audienceKind', 'Контингент', 'AudienceKind', {
                required: true,
                example: 'GROUP',
                rule: 'GROUP — група, STREAM — потік, SUBGROUP — підгрупа; заповнюється відповідна колонка нижче',
            }),
            ref('groupId', 'Група', 'groups', { example: 'GRP-2026-0001', rule: 'Для контингенту GROUP' }),
            ref('streamId', 'Потік', 'streams', { example: 'STR-2026-001', rule: 'Для контингенту STREAM' }),
            ref('subgroupId', 'Підгрупа', 'subgroups', { example: 'GRP-2026-0001-A', rule: 'Для контингенту SUBGROUP' }),
            {
                key: 'mainTeacher',
                label: 'Викладач',
                type: 'ref',
                ref: 'teachers',
                column: 'teacher_code',
                example: 'TCH-0001',
                rule: 'Основний викладач',
                through: { relation: 'teachers', model: 'AssignmentTeacher', ownKey: 'assignmentId', otherKey: 'teacherId', fixed: { role: 'MAIN' } },
                width: 16,
            },
            {
                key: 'coTeachers',
                label: 'Співвикладачі',
                type: 'refMany',
                ref: 'teachers',
                column: 'co_teacher_codes',
                example: 'TCH-0002',
                rule: 'Коди через кому',
                through: { relation: 'teachers', model: 'AssignmentTeacher', ownKey: 'assignmentId', otherKey: 'teacherId', fixed: { role: 'CO' } },
                hidden: true,
                width: 20,
            },
            int('hours', 'Годин', { required: true, min: 0, example: '30' }),
            int('pairsTotal', 'Пар усього', { readOnly: true, rule: 'Обчислюється: години / академічних годин у парі, вгору (D10)' }),
            enumField('studyMode', 'Режим', 'StudyMode', { example: 'WEEKLY' }),
            int('weeklyUnits', 'Пар щотижня', { min: 0, example: '1', rule: 'WEEKLY: пар на тиждень (щотижня)' }),
            int('halfUnits', 'Пар раз на 2 тижні', { min: 0, example: '0', rule: 'WEEKLY: пар через тиждень (чисельник/знаменник)' }),
            int('weekFrom', 'З тижня', { min: 1, example: '1', hidden: true }),
            int('weekTo', 'По тиждень', { min: 1, example: '15', hidden: true }),
            int('cycleDays', 'Днів циклу', { min: 1, example: '10', hidden: true, rule: 'CYCLE: навчальних днів у блоці' }),
            int('cyclePairsPerDay', 'Пар на день циклу', { min: 1, max: 8, example: '3', hidden: true }),
            date('cycleWindowFrom', 'Вікно циклу з', { hidden: true }),
            date('cycleWindowTo', 'Вікно циклу по', { hidden: true }),
            ref('requirementId', 'Вимога до заняття', 'lesson_requirements', { example: 'REQ-0001', hidden: true, rule: 'Порожньо — підбирається за дисципліною й видом заняття' }),
            {
                key: 'roomTypes',
                label: 'Типи аудиторій (перевизначення)',
                type: 'dictItems',
                dictionary: 'room_type',
                column: 'room_type_codes',
                example: 'LAB',
                rule: 'Коди через кому; порожньо — з вимоги',
                through: { relation: 'roomTypes', model: 'AssignmentRoomType', ownKey: 'assignmentId', otherKey: 'roomTypeId' },
                hidden: true,
                width: 20,
            },
            int('minCapacity', 'Мін. місць (перевизначення)', { min: 0, hidden: true }),
            {
                key: 'equipment',
                label: 'Обладнання (перевизначення)',
                type: 'dictItems',
                dictionary: 'equipment',
                column: 'equipment_codes',
                example: 'PC',
                rule: 'Коди через кому; порожньо — з вимоги',
                through: { relation: 'equipment', model: 'AssignmentEquipment', ownKey: 'assignmentId', otherKey: 'equipmentId' },
                hidden: true,
                width: 20,
            },
            dict('formatId', 'Формат', 'lesson_format', { example: 'OFFLINE', rule: 'Порожньо — з вимоги' }),
            bool('departmentRoomsOnly', 'Лише аудиторії кафедри', { hidden: true, rule: 'так / ні; порожньо — з вимоги' }),
            bool('teacherRoomRequired', 'Аудиторія викладачу для онлайн', { hidden: true, rule: 'так / ні; порожньо — з вимоги' }),
            dict('languageId', 'Мова викладання', 'language', { example: 'UK', hidden: true, rule: 'Порожньо — мова групи' }),
            enumField('status', 'Статус', 'AssignmentStatus', { example: 'DRAFT' }),
            note,
        ],
        dependsOn: [
            'semesters',
            'curriculum_items',
            'subjects',
            'lesson_type',
            'departments',
            'groups',
            'streams',
            'subgroups',
            'teachers',
            'lesson_requirements',
            'room_type',
            'equipment',
            'lesson_format',
            'language',
        ],
        titleField: 'code',
        searchFields: ['code', 'note'],
        defaultSort: { field: 'code', dir: 'asc' },
        softDelete: true,
        customPage: 'assignments',
    },
    // ── Правила ──
    {
        code: 'scheduling_rules',
        kind: 'entity',
        title: 'Правила розкладу',
        titleSingular: 'Правило',
        description: 'Правила формування розкладу (пакет даних №15): максимум пар на день і поспіль, мінімальна перерва, максимум переходів тощо; область — університет, факультет або курс.',
        group: 'Правила',
        prismaModel: 'SchedulingRule',
        sheet: '29_Scheduling_Rules',
        who: WHO_OFFICE,
        fields: [
            code('RULE-001'),
            str('key', 'Вид правила', {
                required: true,
                example: 'maxPairsPerDayStudent',
                rule: 'Технічний ключ правила (maxPairsPerDayStudent, maxConsecutivePairs, sameSubjectOncePerDay, …)',
                width: 26,
            }),
            str('name', 'Назва', { required: true, example: 'Максимум пар на день для студента', width: 36 }),
            { key: 'value', label: 'Значення', type: 'json', required: true, example: '4', rule: 'Число, так/ні або JSON', width: 12 },
            enumField('scope', 'Область', 'RuleScope', { example: 'ALL' }),
            ref('facultyId', 'Факультет', 'faculties', { example: 'FAC-01', rule: 'Для області FACULTY' }),
            int('course', 'Курс', { min: 1, max: 6, rule: 'Для області COURSE' }),
            text('description', 'Опис'),
            isActive,
        ],
        dependsOn: ['faculties'],
        titleField: 'name',
        searchFields: ['code', 'key', 'name'],
        defaultSort: { field: 'code', dir: 'asc' },
        softDelete: true,
    },
    {
        code: 'weight_profiles',
        kind: 'entity',
        title: 'Профілі ваг',
        titleSingular: 'Профіль ваг',
        description: 'Ваги м’яких обмежень генератора (SPEC 7.4). Кілька профілів в одному запуску дають альтернативні варіанти розкладу (D16, ТЗ §45).',
        group: 'Правила',
        prismaModel: 'WeightProfile',
        sheet: '30_Weight_Profiles',
        who: WHO_OFFICE,
        fields: [
            code('BALANCED'),
            str('name', 'Назва', { required: true, example: 'Збалансований', width: 26 }),
            text('description', 'Опис', { hidden: false }),
            {
                key: 'weights',
                label: 'Ваги',
                type: 'json',
                required: true,
                example: '{"studentWindow":100,"transition":80}',
                rule: 'JSON: ключі SolverWeights (studentWindow, teacherWindow, transition, …)',
                width: 30,
                hidden: true,
            },
            bool('isDefault', 'За замовчуванням'),
            bool('isSystem', 'Системний', { readOnly: true }),
            sortOrder,
            isActive,
        ],
        dependsOn: [],
        titleField: 'name',
        searchFields: ['code', 'name'],
        defaultSort: { field: 'sortOrder', dir: 'asc' },
        softDelete: true,
    },
];
// ─────────────────────────────── Збирання реєстру ───────────────────────────────
const snake = (s) => s.replace(/([a-z0-9])([A-Z])/g, '$1_$2').toLowerCase();
/** Технічне ім'я колонки XLSX за замовчуванням: facultyId → faculty_code, meta.needsRoom → needs_room. */
export function defaultColumn(field) {
    const base = field.key.startsWith('meta.') ? field.key.slice('meta.'.length) : field.key;
    if ((field.type === 'ref' || field.type === 'dictItem') && base.endsWith('Id'))
        return `${snake(base.slice(0, -2))}_code`;
    if (field.type === 'refMany' || field.type === 'dictItems')
        return `${snake(base)}_codes`;
    return snake(base);
}
/** Службові колонки (D6, D39): імпортуються в meta. */
const SERVICE_FIELDS = [
    {
        base: 'status',
        label: 'Статус даних',
        example: 'Відкриті дані',
        rule: `Службова колонка (D39): ${DATA_STATUS_VALUES.map((v) => `«${v}»`).join(', ')}`,
    },
    { base: 'source', label: 'Джерело даних', example: 'https://example.edu.ua/…', rule: 'Службова колонка: звідки взято значення (URL, документ)' },
    { base: 'note', label: 'Примітка до даних', example: 'Уточнити в деканаті', rule: 'Службова колонка' },
];
function buildEntry(input, order) {
    const fields = input.fields.map((f) => ({ ...f, column: f.column ?? defaultColumn(f), required: f.required ?? false }));
    const taken = new Set(fields.map((f) => f.column));
    for (const s of SERVICE_FIELDS) {
        const column = taken.has(s.base) ? `data_${s.base}` : s.base;
        fields.push({
            key: `meta.${s.base}`,
            label: s.label,
            type: 'string',
            column,
            required: false,
            example: s.example,
            rule: s.rule,
            hidden: true,
            width: 20,
            service: true,
        });
    }
    const { fields: _fields, naturalKey, hasDemoFlag, ...rest } = input;
    return {
        ...rest,
        order,
        fields,
        naturalKey: naturalKey ?? ['code'],
        hasDemoFlag: hasDemoFlag ?? false,
        fileName: `${input.sheet}.xlsx`,
    };
}
/** Реєстр довідників у порядку імпорту (спершу класифікатори, далі структурні за залежностями). */
export const DIRECTORY_REGISTRY = [...CLASSIFIERS, ...ENTITIES].map((e, i) => buildEntry(e, i + 1));
/** Довідник за кодом. */
export const DIRECTORY_BY_CODE = Object.fromEntries(DIRECTORY_REGISTRY.map((e) => [e.code, e]));
/** Коди класифікаторів (= Dictionary.code). */
export const CLASSIFIER_CODES = DIRECTORY_REGISTRY.filter((e) => e.kind === 'dictionary').map((e) => e.code);
/** Довідник за кодом; кидає помилку для невідомого коду. */
export function getDirectory(code) {
    const entry = DIRECTORY_BY_CODE[code];
    if (!entry)
        throw new Error(`Невідомий довідник: ${code}`);
    return entry;
}
/** Поле довідника за ключем або технічним ім'ям колонки. */
export function getDirectoryField(entry, keyOrColumn) {
    return entry.fields.find((f) => f.key === keyOrColumn || f.column === keyOrColumn);
}
/** Групи меню з довідниками в порядку показу (підпорядковані довідники — лише всередині батьківських). */
export function directoriesByGroup(includeChildren = false) {
    return DIRECTORY_GROUPS.map((group) => ({
        group,
        items: DIRECTORY_REGISTRY.filter((e) => e.group === group && (includeChildren || !e.parent)),
    })).filter((g) => g.items.length > 0);
}
/**
 * Порядок імпорту для набору довідників (топологічне сортування за dependsOn, стабільне щодо order).
 * Кидає помилку на циклі залежностей.
 */
export function importOrder(codes = DIRECTORY_REGISTRY.map((e) => e.code)) {
    const wanted = new Set(codes);
    const result = [];
    const state = new Map();
    const visit = (code, path) => {
        const s = state.get(code);
        if (s === 'done')
            return;
        if (s === 'visiting')
            throw new Error(`Цикл залежностей довідників: ${[...path, code].join(' → ')}`);
        state.set(code, 'visiting');
        for (const dep of getDirectory(code).dependsOn)
            if (dep !== code && wanted.has(dep))
                visit(dep, [...path, code]);
        state.set(code, 'done');
        result.push(code);
    };
    const sorted = [...wanted].sort((a, b) => getDirectory(a).order - getDirectory(b).order);
    for (const code of sorted)
        visit(code, []);
    return result;
}
//# sourceMappingURL=registry.js.map